import styled from 'styled-components';
import theme from 'src/theme';

const DECK_TANK_HEIGHT_PERCENT = 17;

const DEFAULT_FONT_SIZE = '0.55rem';
const DEFAULT_LINE_HEIGHT = '0.7rem';
const REPORT_DEFAULT_FONT_SIZE = '1rem';
const REPORT_DEFAULT_LINE_HEIGHT = '1.4';
const DEFAULT_ZOOM_FONT_SIZE = '0.95rem';
const DEFAULT_ZOOM_LINE_HEIGHT = '1.4';

type DynamicSizeProps = {
    containerWidth?: number;
    minWidth?: number;
};

export const TankPlanWrapper = styled.div<DynamicSizeProps>`
    height: 100%;
    width: ${(props) => (props.containerWidth ? props.containerWidth + 'px' : '100%')};
    position: relative;
    margin: 0;
    top: -30px;
    padding: 40px 2px 0;
    overflow: hidden; // Changed: prevent double scrollbars
    background: ${({ theme }) => theme.colors.background.lightGrey};
`;

export const ScrollContainer = styled.div<DynamicSizeProps>`
    height: 100%;
    width: 100%;
    overflow-x: ${(props) => (props.minWidth && props.containerWidth && props.minWidth > props.containerWidth ? 'scroll' : 'hidden')};
    overflow-y: hidden;
    > div {
        height: 100%; // Add height 100% to ensure transform wrapper takes full height
        min-width: ${(props) => (props.minWidth ? props.minWidth + 'px' : 'auto')};
        > div {
            height: 100%; // Add height 100% to ensure transform component takes full height
        }
    }
`;

export const TankPlanInsideWrapper = styled.div<DynamicSizeProps>`
    height: 100%;
    width: ${(props) => (props.containerWidth ? props.containerWidth + 'px' : '100%')};
    min-width: ${(props) => (props.minWidth ? props.minWidth + 'px' : 'auto')};
    position: relative;
    margin: 0;
`;

export const TankPlanViewWrapper = styled.div`
    height: 100%;
    width: '100%;
`;

export const DeckTanksWrapper = styled.div<DynamicSizeProps>`
    height: ${DECK_TANK_HEIGHT_PERCENT}%; // Fixed height for deck tanks row
    display: flex;
    width: 100%;
    padding: 2px;
`;

type DeckTankProps = {
    x: number;
    width: number;
};

export const DeckTankStyle = styled.div<DeckTankProps>`
    position: relative;
    height: 100%;
    width: ${(props) => props.width}%;
    margin-left: ${(props) => props.x}%;
`;

export const TankPlanColumns = styled.div`
    display: flex;
    height: 68%;
    grid-gap: 0px;
    background: ${({ theme }) => theme.colors.background.lightGrey};
`;

type TankColumnProps = {
    flex: number;
};

export const TankColumnStyle = styled.div<TankColumnProps>`
    flex: ${(props) => props.flex};
    display: flex;
    width: 100%
    height: 100%;
    flex-direction: column;
    grid-gap: 0px;
`;

type TankRowProps = {
    flex: number;
};

export const TankRowStyle = styled.div<TankRowProps>`
    flex: ${(props) => props.flex};
    flex-direction: row;
    display: flex;
    grid-gap: 0px;
    overflow: hidden;
`;

type TankContainerProps = {
    cofferdams?: string[];
    isZoomed?: boolean;
    isReport?: boolean;
};

export const TankContainerStyle = styled.div<TankContainerProps>`
    display: flex;
    width: 100%;
    flex: 1;
    height: 100%;
    ${(props) => props?.cofferdams?.map((c) => `border-${c}: 3px solid ${theme.colors.text.black}; `)}
    margin: 1px;
    padding: ${(props) => (props.isZoomed ? '4px' : '2px')};
`;

type TankProps = {
    isSelected: boolean;
    color: string;
    isDimmed: boolean;
    isZoomed?: boolean;
    isReport?: boolean;
};
export const TankStyle = styled.div<TankProps>`
    font-size: ${(props) => {
        if (props.isReport) return REPORT_DEFAULT_FONT_SIZE;
        if (props.isZoomed) return DEFAULT_ZOOM_FONT_SIZE;
        return DEFAULT_FONT_SIZE;
    }};
    line-height: ${(props) => {
        if (props.isReport) return REPORT_DEFAULT_LINE_HEIGHT;
        if (props.isZoomed) return DEFAULT_ZOOM_LINE_HEIGHT;
        return DEFAULT_LINE_HEIGHT;
    }};

    /* Layout */
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    height: 100%;
    width: 100%;
    position: relative;

    /* Appearance */
    border-radius: 4px;
    background: ${(props) => {
        if (props.isDimmed) return theme.colors.background.grey;
        if (props.color) return `#${props.color}`;
        return theme.colors.background.white;
    }};
    border: ${(props) =>
        props.isSelected && !props.isReport ? `3px dashed ${theme.colors.border.selected}` : `1px solid ${theme.colors.border.aaa}`};

    /* Behavior */
    z-index: 0;
    cursor: ${(props) => (props.isZoomed ? 'default' : 'pointer')};
    transition: border-color 0.3s ease;

    /* States */
    &.is-dimmed {
        opacity: 0.3;
    }

    ${(props) =>
        !props.isZoomed &&
        `
        &:hover {
            background: ${theme.colors.background.lightGrey};
            transition: none;
        }
    `}
    padding: ${(props) => (props.isZoomed ? '4px' : '2px')};
`;

export const TankDetailsRows = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    justify-content: flex-end;
`;

export const TankDetailsRow = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;

    &.trade-name-row,
    &.ports-row,
    &.cargo-number-row {
        display: block;
        margin: 0 0 4px 0;
        font-weight: 700;
    }

    &.cargo-number-row {
        margin: 0 0 1px 0;
    }

    &.text-overflow-2-lines {
        text-overflow: ellipsis;
        overflow-wrap: break-word;
        display: block;
        word-wrap: inherit;
        white-space: pre-wrap;
    }
`;

type ZoomTankProps = {
    isZoomed?: boolean;
    isReport?: boolean;
};

export const TankDetailsCargoNumbers = styled.div<ZoomTankProps>`
    font-size: ${(props) => {
        if (props.isReport) return REPORT_DEFAULT_FONT_SIZE;
        if (props.isZoomed) return DEFAULT_ZOOM_FONT_SIZE;
        return DEFAULT_FONT_SIZE;
    }};
`;

export const TankDetailsCenter = styled.div`
    height: 100%;
    overflow: hidden;
`;

type TankDetailsCenterRowProps = {
    maxOneLine?: boolean;
    bold?: boolean;
    isZoomed?: boolean;
};

export const TankDetailsCenterRow = styled.div<TankDetailsCenterRowProps>`
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;
    text-align: center;
    ${(props) => props.bold && `font-weight: bold;`}
    text-overflow: ellipsis;
    ${(props) => props.maxOneLine && `white-space: nowrap;`}
    overflow: hidden;
    &.cargo-number {
        display: flex;
        justify-content: space-between;
    }
`;

export const FillWrapper = styled.div`
    flex: 0 0 auto;
    justify-content: space-between;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: calc(100% - 10px);
`;

export const TankDetailsText = styled.div`
    .unloading-port {
        margin-left: 5px;
    }

    &.small-amount {
        font-weight: bold;
    }
`;

export const ProgressBarContainer = styled.div`
    width: 10px;
    min-width: 10px;
    padding: 0 2px;
    height: 25px;
`;

export const ProgressBarOuter = styled.div`
    width: 100%;
    height: 100%;
    background-color: white;
    border: 1px solid #999;
    position: relative;
    border-radius: 4px;
    overflow: hidden;
`;

type ProgressBarInnerProps = {
    percentage?: number | null;
};

export const ProgressBarInner = styled.div<ProgressBarInnerProps>`
    background-color: black;
    width: 100%;
    position: absolute;
    bottom: 0;
    height: ${(props) => props?.percentage ?? 0}%;
`;

export const OverlayMenuStyle = styled.div`
    position: fixed;
    top: 25%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: fit-content;
    height: fit-content;
    background: white;
    box-shadow: #ccc 0 2px 6px 2px;
    z-index: 2;
`;

export const WarningTriangle = styled.div`
    display: inline-flex;
    font-size: 0.85rem;
    .violation-text {
        font-size: 0.75rem;
        line-height: 1.4rem;
    }
`;

type TriangleProps = {
    hasBackground?: boolean;
};
export const YellowTriangle = styled.div<TriangleProps>`
    color: ${theme.legacyColors.softErrorText};
    ${(props) => props.hasBackground && `border: 2px ;`}
    ${(props) => props.hasBackground && `background-color: ${theme.legacyColors.headingPanelBgColor};`}
    ${(props) => props.hasBackground && `border-radius: 4px;`}
    ${(props) => props.hasBackground && `padding: 2px 3px;`}
    ${(props) => props.hasBackground && `font-size: 0.65rem;`}
`;

export const RedTriangle = styled.div<TriangleProps>`
    color: ${theme.legacyColors.error};
    ${(props) => props.hasBackground && `border: 2px ;`}
    ${(props) => props.hasBackground && `background-color: ${theme.legacyColors.headingPanelBgColor};`}
    ${(props) => props.hasBackground && `border-radius: 4px;`}
    ${(props) => props.hasBackground && `padding: 2px 3px;`}
    ${(props) => props.hasBackground && `font-size: 0.65rem;`}
`;

export const OperationTypeIndicator = styled.div`
    font-size: 0.65rem;
    font-weight: bold;
    width: fit-content;
    height: fit-content;
    border-radius: 30%;
    color: white;
    padding: 1px 3px;
    background: ${theme.legacyColors.darkBlue};
`;

export const TankCurtain = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    background: #fff;
    opacity: 0.1;
    z-index: 1;
`;

export const TankMenu = styled.button`
    position: absolute;
    background: ${({ theme }) => theme.colors.background.white};
    z-index: 2;
    width: 2.5em;
    height: 2.5em;
    @media only screen and (max-width: 1920px) {
        font-size: 0.6rem;
    }
    box-shadow: -4px -2px 12px -2px ${({ theme }) => theme.colors.shadow.ccc};
    border-radius: 4px 0 0 0;
    text-align: center;
    cursor: pointer;
    border: 0;
`;

export const TankFillingMenu = styled(TankMenu)`
    bottom: 0;
    right: 0;
`;

export const TankInfoMenu = styled(TankMenu)`
    top: 0;
    right: 0;
    z-index: 4;
`;

export const CargoInfoMenu = styled(TankMenu)`
    top: 0;
    left: 0;
`;

export const BlockedTankStyle = styled.div`
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;

    .ban-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 1.35rem;
        color: ${({ theme }) => theme.colors.text.error};
        z-index: 4;
    }

    .curtain {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
        background: #fff;
        opacity: 0.5;
    }
`;

export const TanksInfoWrapper = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
    overflow: auto;
    padding: 5px 0;
`;

export const TankInfo = styled.div`
    padding: 5px;
    background: #fafafa;
    font-size: 0.75rem;
`;

export const ConditionsMenuWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-right: 15px;
    border: 0;
    padding: 4px;
    padding: 0 10px 0 0;

    .menu-item {
        font-size: 0.8rem;
        font-weight: 500;
        cursor: pointer;
    }

    button {
        display: flex;
        gap: 10px;
        border: 0;
        border-radius: 4px;
        background: #fff;
        align-items: center;
    }
`;

export const OptionsMenuWrapper = styled.div`
    position: relative;
    display: flex;
    gap: 10px;
    margin-left: 10px;
    border: 0;
    border-radius: 4px;
    padding: 4px;

    .menu-item {
        font-size: 0.8rem;
        font-weight: 500;
        cursor: pointer;
    }

    button {
        display: flex;
        gap: 10px;
        border: 0;
        border-radius: 4px;
        background: #fff;
        align-items: center;
    }
`;

export const TankPlanContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0; // Add this to ensure flex child doesn't overflow
    padding: 0 3px 0px 0px;
`;

export const MainTanksWrapper = styled.div<{ $hasDeckTanks: boolean }>`
    height: ${(props) => (props.$hasDeckTanks ? `${100 - 2 * DECK_TANK_HEIGHT_PERCENT}%` : '100%')}; // Adjust main height based on deck tanks
    position: relative;
    width: 100%;
`;

export const ToggleWrapper = styled.div`
    margin-left: 16px;
    display: flex;
    align-items: center;
`;

export const ToggleCheckbox = styled.input`
    margin-right: 8px;
    cursor: pointer;
    width: 16px;
    height: 16px;
`;

export const ToggleLabel = styled.label`
    cursor: pointer;
    font-size: 14px;
`;

export const TankZoomMenu = styled(TankMenu)`
    bottom: 0;
    left: 0;
    z-index: 4;
`;

export const ZoomedTankOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

export const ZoomedTankContainer = styled.div<{ top?: number; left?: number }>`
    position: absolute;
    width: 300px;
    height: 200px;
    top: ${(props) => (props.top !== undefined ? `${props.top}px` : '50%')};
    left: ${(props) => (props.left !== undefined ? `${props.left}px` : '50%')};
    transform: ${(props) => (props.top === undefined || props.left === undefined ? 'translate(-50%, -50%)' : 'none')};
    background-color: ${({ theme }) => theme.colors.background.white};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    padding: 0;
    overflow: hidden;
    display: flex;
    cursor: pointer; /* Add cursor pointer to indicate it's clickable */

    /* Make the inner tank container take full space without borders */
    > div {
        width: 100%;
        height: 100%;

        /* Target the tank container to remove borders */
        > div {
            margin: 0;
            border: none;
        }
    }
`;
